/**
 * Funnel del mes por etapa.
 *
 * Tres decisiones que no son de estilo:
 *
 *  - **Las columnas no se ordenan.** Un funnel reordenado por conversión deja de
 *    ser un funnel: las etapas solo significan algo en su orden. La tabla se lee
 *    de arriba abajo y el ancho de la barra hace el estrechamiento.
 *  - **Cada porcentaje publica su base.** La conversión dice "88 de 412
 *    Reuniones" y el delta dice "contra junio 2026 (412)". Sin eso el número no
 *    se puede recalcular a mano, que es la única forma de auditar un panel.
 *  - **Una etapa sin cargar no vale 0.** Muestra "Sin dato": un 0 en una barra
 *    del funnel se lee como un mes sin leads.
 */
import { SIN_DATO } from '@ops/contracts'

import {
  formatMetricValue,
  formatPeriodo,
  formatPeriodoCorto,
} from '../../lib/format'
import {
  type Column,
  DataTable,
  DeltaTag,
  EmptyMetricState,
  HealthDot,
  Panel,
  ProgressBar,
  ProvenanceBadge,
} from '../../ui'
import { etiquetaEtapa, type FilaEtapa } from './derivar'

export interface FunnelBlockProps {
  filas: readonly FilaEtapa[]
  periodo: string
  /** Período contra el que se compara. `null` = no hay comparación posible. */
  periodoBase: string | null
  /**
   * Mensaje de la API cuando el mes anterior no se pudo traer. Se publica en el
   * pie: un delta ausente sin explicación parece un bug del panel.
   */
  errorComparacion?: string | null
  /** Reloj de la pantalla, para que la frescura sea la misma en todos lados. */
  now: Date
}

export function FunnelBlock({
  filas,
  periodo,
  periodoBase,
  errorComparacion,
  now,
}: FunnelBlockProps) {
  const columnas: ReadonlyArray<Column<FilaEtapa>> = [
    {
      key: 'etapa',
      header: 'Etapa',
      width: '150px',
      rowHeader: true,
      render: (fila) => fila.nombre,
    },
    {
      key: 'volumen',
      header: `Volumen de ${formatPeriodoCorto(periodo)}`,
      width: '230px',
      render: (fila) =>
        fila.cantidad === null ? (
          <EmptyMetricState
            metricNombre={`${fila.nombre} de ${formatPeriodo(periodo)}`}
            detalle="nadie cargó el agregado del mes"
            size="sm"
          />
        ) : (
          <div className="flex items-center gap-2">
            <span className="tabular w-14 shrink-0 text-right text-titulo font-semibold text-text">
              {formatMetricValue(fila.cantidad, 'count')}
            </span>
            <ProgressBar
              pct={fila.anchoPct}
              label={`Volumen de ${fila.nombre} en ${formatPeriodo(periodo)}`}
              severidad={fila.health}
              etiqueta={null}
              size="sm"
              className="flex-1"
            />
          </div>
        ),
    },
    {
      key: 'delta',
      header: 'Contra el mes anterior',
      width: '230px',
      render: (fila) => {
        if (fila.deltaPct !== null) {
          return (
            <DeltaTag
              deltaPct={fila.deltaPct}
              base={fila.base}
              unidad="count"
              direccion="higher_better"
              size="sm"
            />
          )
        }
        if (periodoBase === null) {
          return (
            <span className="text-micro text-muted-strong">
              sin período anterior con el que comparar
            </span>
          )
        }
        return (
          <span className="text-micro text-muted-strong">
            {SIN_DATO} sin dato de {formatPeriodoCorto(periodoBase)} para
            comparar
          </span>
        )
      },
    },
    {
      key: 'conversion',
      header: 'Conversión desde la etapa previa',
      width: '210px',
      render: (fila) => {
        if (fila.conversionPct === null || fila.baseConversion === null) {
          return (
            <span className="text-micro text-muted-strong">
              {SIN_DATO}{' '}
              {fila.orden === 1
                ? 'es la entrada del funnel'
                : 'sin base para calcularla'}
            </span>
          )
        }
        return (
          <div className="min-w-0">
            <span className="tabular text-base font-semibold text-text">
              {formatMetricValue(fila.conversionPct, 'percent')}
            </span>
            {/* La base del porcentaje, en la misma celda: sin esto no se puede
                recalcular a mano y el número no se puede defender. */}
            <span className="tabular ml-1.5 text-micro text-muted-strong">
              {formatMetricValue(fila.cantidad, 'count')} de{' '}
              {formatMetricValue(fila.baseConversion.cantidad, 'count')}{' '}
              {fila.baseConversion.nombre}
            </span>
          </div>
        )
      },
    },
    {
      key: 'estado',
      header: 'Lectura',
      width: '150px',
      render: (fila) => (
        <HealthDot health={fila.health} label={etiquetaEtapa(fila)} size="sm" />
      ),
    },
    {
      key: 'fuente',
      header: 'Fuente',
      render: (fila) => (
        <ProvenanceBadge
          provenance={fila.provenance}
          now={now}
          variant="inline"
          hrefAdmin="/admin"
        />
      ),
    },
  ]

  return (
    <Panel
      titulo={`Etapas de ${formatPeriodo(periodo)}`}
      subtitulo="Se lee de arriba abajo: el orden de las etapas es el dato."
      padding="none"
      footer={
        errorComparacion !== null && errorComparacion !== undefined ? (
          <span role="alert">
            No pudimos traer{' '}
            {periodoBase === null ? 'el mes anterior' : formatPeriodo(periodoBase)}{' '}
            para comparar: {errorComparacion}
          </span>
        ) : undefined
      }
    >
      <DataTable
        columns={columnas}
        rows={filas}
        rowKey={(fila) => fila.id}
        rowSeverity={(fila) => fila.health}
        densidadPorSeveridad
        emptyMessage="No hay etapas de funnel configuradas todavía"
        caption={
          <>
            Funnel de {formatPeriodo(periodo)} por etapa, con la conversión contra
            la etapa previa y la variación contra{' '}
            {periodoBase === null
              ? 'el mes anterior'
              : formatPeriodo(periodoBase)}
            .
          </>
        }
      />
    </Panel>
  )
}
